import { directSupabase } from '../lib/supabaseClient';
import { generateUuid } from '../utils/uuid-helper';

export const WORDPRESS_MEDIA_BUCKET = 'wordpress-media';
export const MAX_WORDPRESS_MEDIA_BYTES = 20 * 1024 * 1024;
export const ACCEPTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
] as const;

const BUCKET_SETUP_MSG = `Create the bucket in Supabase Dashboard: Storage → New bucket → Name: "${WORDPRESS_MEDIA_BUCKET}" → Public: On → Create. Then add policies so authenticated users can upload and delete.`;

export type WordpressSeoFields = {
  altText: string;
  title: string;
  caption: string;
  description: string;
  filename: string;
};

export type WordpressMediaRow = {
  id: string;
  original_filename: string;
  original_mime_type: string | null;
  webp_filename: string;
  storage_path: string;
  public_url: string;
  file_size_bytes: number | null;
  width: number | null;
  height: number | null;
  alt_text: string;
  title: string;
  caption: string;
  description: string;
  keywords: string | null;
  is_overlay?: boolean;
  source_media_id?: string | null;
  overlay_primary_label?: string | null;
  overlay_secondary_label?: string | null;
  overlay_info_position?: string | null;
  overlay_logo_position?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type WordpressOverlayMeta = {
  sourceMediaId?: string | null;
  primaryLabel: string;
  secondaryLabel: string;
  infoPosition: string;
  logoPosition: string;
};

export type ConvertedWebpImage = {
  blob: Blob;
  filename: string;
  width: number;
  height: number;
};

const ALLOWED_MIME = new Set<string>(ACCEPTED_IMAGE_MIME_TYPES);

export function isAcceptedImageFile(file: File): boolean {
  const mime = String(file.type || '').toLowerCase();
  if (ALLOWED_MIME.has(mime)) return true;
  return /\.(jpe?g|png|webp|gif|bmp)$/i.test(file.name);
}

export function slugifyOriginalName(filename: string): string {
  const base = String(filename || '')
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'image';
}

export function toWebpFilename(originalFilename: string, uniqueId = generateUuid()): string {
  return `${slugifyOriginalName(originalFilename)}-${uniqueId.slice(0, 8)}.webp`;
}

export function toSeoWebpFilename(name: string, fallback = 'image'): string {
  const slug = slugifyOriginalName(name || fallback).slice(0, 80);
  return `${slug || fallback}.webp`;
}

export function formatWordpressFieldsForCopy(row: Pick<
  WordpressMediaRow,
  'title' | 'alt_text' | 'caption' | 'description' | 'public_url' | 'webp_filename'
>): string {
  return [
    `Alt Text: ${row.alt_text || ''}`,
    `Title: ${row.title || ''}`,
    `Caption: ${row.caption || ''}`,
    `Description: ${row.description || ''}`,
    `Filename: ${row.webp_filename || ''}`,
    `URL: ${row.public_url || ''}`,
  ].join('\n');
}

export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number
): Promise<Blob> {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, quality);
  });
  if (!blob) {
    throw new Error(`This browser could not encode ${mime}`);
  }
  return blob;
}

export async function convertImageFileToWebp(
  file: File,
  options?: { quality?: number }
): Promise<ConvertedWebpImage> {
  if (typeof document === 'undefined') {
    throw new Error('Image conversion is only available in the browser');
  }
  if (file.size > MAX_WORDPRESS_MEDIA_BYTES) {
    throw new Error('File too large. Please upload an image under 20MB.');
  }
  if (!isAcceptedImageFile(file)) {
    throw new Error('Unsupported file type. Please upload a JPEG, PNG, WebP, GIF or BMP image.');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(objectUrl);
    const quality = options?.quality ?? 0.82;
    const width = Math.max(1, img.naturalWidth || img.width || 1);
    const height = Math.max(1, img.naturalHeight || img.height || 1);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare the image for conversion');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, 'image/webp', quality);
    return {
      blob,
      filename: toWebpFilename(file.name),
      width,
      height,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read image for SEO generation'));
    reader.readAsDataURL(blob);
  });
}

export async function rasterizeToJpegDataUrl(source: Blob, maxSidePx = 1280): Promise<string> {
  if (typeof document === 'undefined') {
    return blobToDataUrl(source);
  }
  const objectUrl = URL.createObjectURL(source);
  try {
    const img = await loadImageElement(objectUrl);
    const sourceW = Math.max(1, img.naturalWidth || img.width || 1);
    const sourceH = Math.max(1, img.naturalHeight || img.height || 1);
    const scale = Math.min(1, maxSidePx / Math.max(sourceW, sourceH));
    const width = Math.max(1, Math.round(sourceW * scale));
    const height = Math.max(1, Math.round(sourceH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blobToDataUrl(source);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.72);
    return blobToDataUrl(blob);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function imageFileToAiDataUrl(file: File): Promise<string> {
  return rasterizeToJpegDataUrl(file);
}

export function parseImagePurifySeoFields(content: string): WordpressSeoFields {
  const trimmed = String(content || '')
    .trim()
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```$/i, '')
    .trim();
  const parsed = JSON.parse(trimmed);
  const rawFilename = String(parsed.filename || parsed.fileName || parsed.webp_filename || '').trim();
  return {
    altText: String(parsed.altText || parsed.alt_text || '').trim(),
    title: String(parsed.title || '').trim(),
    caption: String(parsed.caption || '').trim(),
    description: String(parsed.description || '').trim(),
    filename: rawFilename ? toSeoWebpFilename(rawFilename) : '',
  };
}

function parseFunctionErrorBody(value: unknown): any {
  if (!value) return {};
  if (typeof value === 'object') return value as any;
  try {
    return JSON.parse(String(value));
  } catch {
    return { detail: String(value) };
  }
}

function mapAiError(error: any, result?: any): Error {
  const body = parseFunctionErrorBody(result);
  const detailText = String(body.detail || error?.details || '');
  const combined = `${error?.message || ''} ${detailText} ${body.error || ''}`;
  if (combined.includes('invalid_api_key') || combined.toLowerCase().includes('incorrect api key')) {
    return new Error('Invalid OpenAI API key. Please update the key in Supabase Edge Function secrets.');
  }
  if (error?.status === 401 || String(error?.message || '').includes('401')) {
    return new Error('Image Purify AI function returned 401. The function may still be verifying JWTs; it should be deployed with JWT verification off, like quote suggestions.');
  }
  if (combined.includes('missing_api_key')) {
    return new Error('AI service is not configured. Please check that OPENAI_API_KEY is set in Supabase.');
  }
  if (combined.includes('insufficient_quota')) {
    return new Error('AI service quota exceeded. Please try again later.');
  }
  if (body?.error === 'invalid_image') {
    return new Error('The image could not be sent for SEO generation.');
  }
  if (body?.error === 'upstream_error' || error?.status === 502 || String(error?.message || '').includes('HTTP 502')) {
    const compact = detailText.replace(/\s+/g, ' ').slice(0, 280);
    return new Error(compact
      ? `SEO generation failed: ${compact}`
      : 'SEO generation failed. The vision model rejected the request.');
  }
  if (String(error?.message || '').includes('Failed to send a request') || String(error?.message || '').toLowerCase().includes('failed to fetch')) {
    return new Error('Image Purify could not reach the AI edge function. Deploy supabase/functions/ai-image-seo and confirm OPENAI_API_KEY is set in Supabase.');
  }
  if (error?.message) {
    return new Error(error.message);
  }
  return new Error(body.detail || 'SEO generation failed');
}

export async function invokeAiImageFunction(payload: Record<string, unknown>): Promise<any> {
  let authHeader = '';
  try {
    const { data: { session } } = await (directSupabase as any).auth.getSession();
    if (session?.access_token) authHeader = `Bearer ${session.access_token}`;
  } catch {}

  const resp = await (directSupabase as any).functions.invoke('ai-image-seo', {
    body: payload,
    ...(authHeader ? { headers: { Authorization: authHeader } } : {}),
  });
  let { data, error } = resp as any;

  const shouldFallback = Boolean(
    error && (
      String(error.message || '').includes('Failed to send a request') ||
      error.status === 401 ||
      error.statusCode === 401 ||
      String(error.message || '').includes('401')
    )
  );

  if (shouldFallback) {
    try {
      const baseUrl = (directSupabase as any).functions?._url || `${(directSupabase as any).supabaseUrl}/functions/v1`;
      const url = `${baseUrl}/ai-image-seo`;
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
      const res = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : (anonKey ? { Authorization: `Bearer ${anonKey}` } : {})),
          ...(anonKey ? { apikey: anonKey } : {}),
        },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      if (!res.ok) {
        error = { message: `HTTP ${res.status}`, status: res.status, details: text };
        try { data = JSON.parse(text); } catch { data = { detail: text }; }
      } else {
        data = JSON.parse(text);
        error = null;
      }
    } catch (err: any) {
      error = { message: err?.message || 'Direct fetch failed' };
    }
  }

  if (error) {
    throw mapAiError(error, data || error.details || error.context);
  }

  const result = (data || {}) as any;
  if (result?.ok === false) {
    throw mapAiError(error, result);
  }

  return result?.data;
}

export async function generateWordpressSeoFields(args: {
  imageUrl?: string;
  imageDataUrl?: string;
  originalFilename?: string;
  keywords?: string;
}): Promise<WordpressSeoFields> {
  const extraPrompt = String(args.keywords || '').trim() || undefined;
  const data = await invokeAiImageFunction({
    task: 'seo',
    imageUrl: args.imageUrl,
    imageDataUrl: args.imageDataUrl,
    originalFilename: args.originalFilename,
    keywords: extraPrompt,
    userPrompt: extraPrompt,
  });
  const seo = parseImagePurifySeoFields(JSON.stringify(data || {}));
  if (!seo.altText && !seo.title && !seo.caption && !seo.description) {
    throw new Error('SEO generation returned empty fields.');
  }
  return seo;
}

export async function listWordpressMedia(): Promise<WordpressMediaRow[]> {
  const { data, error } = await (directSupabase as any)
    .from('wordpress_media')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message || 'Failed to load WordPress media');
  return (data || []) as WordpressMediaRow[];
}

function missingOverlayColumn(error: { message?: string } | null | undefined) {
  const message = String(error?.message || '').toLowerCase();
  return (
    message.includes('schema cache') ||
    ((message.includes('column') || message.includes('could not find')) && (
      message.includes('is_overlay') ||
      message.includes('source_media_id') ||
      message.includes('overlay_')
    ))
  );
}

async function insertWordpressMediaRow(row: Record<string, unknown>) {
  const first = await (directSupabase as any)
    .from('wordpress_media')
    .insert(row)
    .select('*')
    .single();
  if (!first.error || !missingOverlayColumn(first.error)) return first;

  const fallback = { ...row };
  delete fallback.is_overlay;
  delete fallback.source_media_id;
  delete fallback.overlay_primary_label;
  delete fallback.overlay_secondary_label;
  delete fallback.overlay_info_position;
  delete fallback.overlay_logo_position;
  return (directSupabase as any)
    .from('wordpress_media')
    .insert(fallback)
    .select('*')
    .single();
}

export async function storeWordpressWebpFile(args: {
  blob: Blob;
  originalFilename: string;
  originalMimeType?: string | null;
  width: number;
  height: number;
  webpFilename?: string;
  keywords?: string;
  overlay?: WordpressOverlayMeta;
  seoSource?: Blob;
  replaceRow?: WordpressMediaRow;
}): Promise<{ row: WordpressMediaRow; seoError?: string }> {
  const { data: auth } = await directSupabase.auth.getUser();
  if (!auth.user) throw new Error('Not authenticated');

  const replaceRow = args.replaceRow;
  const id = replaceRow?.id || generateUuid();
  const storagePath = replaceRow?.storage_path || `${auth.user.id}/${id}.webp`;
  if (replaceRow) {
    await writeStorageWebp(storagePath, args.blob);
  } else {
    const { error: uploadError } = await directSupabase.storage
      .from(WORDPRESS_MEDIA_BUCKET)
      .upload(storagePath, args.blob, { contentType: 'image/webp', upsert: false });

    if (uploadError) {
      const msg = uploadError.message?.toLowerCase() ?? '';
      if (msg.includes('bucket not found') || msg.includes('not found')) {
        throw new Error(`Storage bucket "${WORDPRESS_MEDIA_BUCKET}" not found. ${BUCKET_SETUP_MSG}`);
      }
      throw new Error(uploadError.message || 'Failed to upload WebP image');
    }
  }

  const { data: urlData } = directSupabase.storage.from(WORDPRESS_MEDIA_BUCKET).getPublicUrl(storagePath);
  let seo: WordpressSeoFields = { altText: '', title: '', caption: '', description: '', filename: '' };
  let seoError: string | undefined;
  try {
    const seoSource = args.seoSource || args.blob;
    const imageDataUrl = await rasterizeToJpegDataUrl(seoSource);
    seo = await generateWordpressSeoFields({
      imageUrl: urlData.publicUrl,
      imageDataUrl,
      originalFilename: args.originalFilename,
      keywords: args.keywords,
    });
  } catch (err) {
    seoError = err instanceof Error ? err.message : 'SEO generation failed';
  }

  const row: Record<string, unknown> = {
    original_filename: args.originalFilename,
    original_mime_type: args.originalMimeType || 'image/webp',
    webp_filename: replaceRow?.webp_filename
      || seo.filename
      || args.webpFilename
      || toSeoWebpFilename(args.originalFilename, 'image'),
    storage_path: storagePath,
    public_url: urlData.publicUrl,
    file_size_bytes: args.blob.size,
    width: args.width,
    height: args.height,
    alt_text: seo.altText,
    title: seo.title || slugifyOriginalName(args.originalFilename).replace(/-/g, ' '),
    caption: seo.caption,
    description: seo.description,
    keywords: args.keywords?.trim() || null,
    created_by: auth.user.id,
  };

  if (args.overlay) {
    row.is_overlay = true;
    row.source_media_id = args.overlay.sourceMediaId || null;
    row.overlay_primary_label = args.overlay.primaryLabel || null;
    row.overlay_secondary_label = args.overlay.secondaryLabel || null;
    row.overlay_info_position = args.overlay.infoPosition || null;
    row.overlay_logo_position = args.overlay.logoPosition || null;
  }

  if (replaceRow) {
    try {
      return { row: await updateWordpressMediaRow(replaceRow.id, row), seoError };
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update WordPress media');
    }
  }

  const { data, error } = await insertWordpressMediaRow(row);
  if (error) {
    await directSupabase.storage.from(WORDPRESS_MEDIA_BUCKET).remove([storagePath]);
    throw new Error(error.message || 'Failed to save WordPress media');
  }

  return { row: data as WordpressMediaRow, seoError };
}

export async function uploadWordpressMedia(args: {
  file: File;
  keywords?: string;
  createdBy?: string | null;
}): Promise<{ row: WordpressMediaRow; seoError?: string }> {
  const converted = await convertImageFileToWebp(args.file);
  const existing = await findExistingPurifyRow(args.file.name);
  return storeWordpressWebpFile({
    blob: converted.blob,
    originalFilename: args.file.name,
    originalMimeType: args.file.type || null,
    width: converted.width,
    height: converted.height,
    webpFilename: converted.filename,
    keywords: args.keywords,
    seoSource: args.file,
    replaceRow: existing || undefined,
  });
}

export async function saveOverlayToLibrary(args: {
  blob: Blob;
  filename: string;
  width: number;
  height: number;
  originalFilename: string;
  overlay: WordpressOverlayMeta;
}): Promise<{ row: WordpressMediaRow; seoError?: string }> {
  const keywords = [args.overlay.primaryLabel, args.overlay.secondaryLabel]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ');
  return storeWordpressWebpFile({
    blob: args.blob,
    originalFilename: args.originalFilename,
    originalMimeType: 'image/webp',
    width: args.width,
    height: args.height,
    webpFilename: args.filename,
    keywords: keywords || undefined,
    overlay: args.overlay,
    seoSource: args.blob,
  });
}

async function updateWordpressMediaRow(id: string, patch: Record<string, unknown>): Promise<WordpressMediaRow> {
  const { data, error } = await (directSupabase as any)
    .from('wordpress_media')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new Error(error.message || 'Failed to update WordPress media');
  return data as WordpressMediaRow;
}

async function writeStorageWebp(storagePath: string, blob: Blob) {
  const bucket = directSupabase.storage.from(WORDPRESS_MEDIA_BUCKET);
  const uploaded = await bucket.upload(storagePath, blob, { contentType: 'image/webp', upsert: true });
  if (!uploaded.error) return;
  await bucket.remove([storagePath]);
  const retry = await bucket.upload(storagePath, blob, { contentType: 'image/webp', upsert: false });
  if (retry.error) {
    throw new Error(retry.error.message || uploaded.error.message || 'Failed to upload WebP image');
  }
}

async function findExistingPurifyRow(originalFilename: string): Promise<WordpressMediaRow | null> {
  const name = String(originalFilename || '').trim();
  if (!name) return null;
  const query = (directSupabase as any)
    .from('wordpress_media')
    .select('*')
    .eq('original_filename', name)
    .order('created_at', { ascending: false })
    .limit(20);
  const { data, error } = await query;
  if (error) {
    if (missingOverlayColumn(error)) {
      const fallback = await (directSupabase as any)
        .from('wordpress_media')
        .select('*')
        .eq('original_filename', name)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return (fallback.data || null) as WordpressMediaRow | null;
    }
    return null;
  }
  const rows = (data || []) as WordpressMediaRow[];
  return rows.find((row) => !row.is_overlay) || null;
}

export async function updateWordpressMediaSeo(
  id: string,
  fields: Partial<WordpressSeoFields> & { keywords?: string }
): Promise<WordpressMediaRow> {
  const patch: Record<string, unknown> = {};
  if (fields.altText !== undefined) patch.alt_text = fields.altText;
  if (fields.title !== undefined) patch.title = fields.title;
  if (fields.caption !== undefined) patch.caption = fields.caption;
  if (fields.description !== undefined) patch.description = fields.description;
  if (fields.filename !== undefined) patch.webp_filename = toSeoWebpFilename(fields.filename, 'image');
  if (fields.keywords !== undefined) patch.keywords = fields.keywords;
  return updateWordpressMediaRow(id, patch);
}

export async function regenerateWordpressMediaSeo(
  row: WordpressMediaRow,
  keywords?: string
): Promise<WordpressMediaRow> {
  let imageDataUrl: string | undefined;
  try {
    const response = await fetch(row.public_url);
    if (response.ok) {
      imageDataUrl = await rasterizeToJpegDataUrl(await response.blob());
    }
  } catch {
    imageDataUrl = undefined;
  }
  const extraPrompt = String(keywords || '').trim() || String(row.keywords || '').trim() || undefined;
  const seo = await generateWordpressSeoFields({
    imageUrl: row.public_url,
    imageDataUrl,
    originalFilename: row.original_filename,
    keywords: extraPrompt,
  });
  return updateWordpressMediaSeo(row.id, {
    altText: seo.altText,
    title: seo.title,
    caption: seo.caption,
    description: seo.description,
    keywords: extraPrompt,
  });
}

export async function getWordpressMediaBlob(row: Pick<WordpressMediaRow, 'storage_path' | 'public_url'>): Promise<Blob> {
  if (row.storage_path) {
    const { data, error } = await directSupabase.storage
      .from(WORDPRESS_MEDIA_BUCKET)
      .download(row.storage_path);
    if (!error && data) return data;
  }
  if (row.public_url) {
    const response = await fetch(row.public_url);
    if (response.ok) return response.blob();
  }
  throw new Error('Failed to download image');
}

export async function saveImageBlobLocally(blob: Blob, filename: string): Promise<void> {
  const file = blob.type ? blob : new Blob([blob], { type: 'image/webp' });
  const picker = (window as Window & {
    showSaveFilePicker?: (options: {
      suggestedName?: string;
      types?: Array<{ description: string; accept: Record<string, string[]> }>;
    }) => Promise<{
      createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }>;
    }>;
  }).showSaveFilePicker;

  if (typeof picker === 'function') {
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [{ description: 'WebP image', accept: { 'image/webp': ['.webp'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(file);
      await writable.close();
      return;
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
      throw err;
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  }
}

export async function downloadWordpressMediaFile(row: Pick<WordpressMediaRow, 'storage_path' | 'public_url' | 'webp_filename'>): Promise<void> {
  const filename = toSeoWebpFilename(row.webp_filename || 'image', 'image');
  const blob = await getWordpressMediaBlob(row);
  await saveImageBlobLocally(blob, filename);
}

export async function deleteWordpressMedia(row: WordpressMediaRow): Promise<void> {
  if (row.storage_path) {
    await directSupabase.storage.from(WORDPRESS_MEDIA_BUCKET).remove([row.storage_path]);
  }
  const { error } = await (directSupabase as any)
    .from('wordpress_media')
    .delete()
    .eq('id', row.id);
  if (error) throw new Error(error.message || 'Failed to delete WordPress media');
}

export async function purgeWordpressMedia(): Promise<number> {
  const rows = await listWordpressMedia();
  const paths = rows.map((row) => row.storage_path).filter(Boolean);
  for (let index = 0; index < paths.length; index += 100) {
    const chunk = paths.slice(index, index + 100);
    const { error: storageError } = await directSupabase.storage
      .from(WORDPRESS_MEDIA_BUCKET)
      .remove(chunk);
    if (storageError) {
      throw new Error(storageError.message || 'Failed to delete stored Image Purify files');
    }
  }

  if (rows.length > 0) {
    const { error } = await (directSupabase as any)
      .from('wordpress_media')
      .delete()
      .not('id', 'is', null);
    if (error) throw new Error(error.message || 'Failed to purge Image Purify records');
  }

  return rows.length;
}
